import { useState, useMemo } from "react";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { DataTable, type TableColumnDef } from "../../components/data-table";
import { Badge } from "../../components/ui/badge";
import { Plus, Edit, Trash2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { toast } from "sonner";
import { CommonPagination } from "../../components/CommonPagination";

const mockScholarships = [
  {
    id: 1,
    name: "Merit Scholarship 2024",
    eligibility: "Above 80%",
    amount: 50000,
    status: "active",
    institutions: "All",
  },
  {
    id: 2,
    name: "Need-Based Support",
    eligibility: "Income < 3 Lakhs",
    amount: 30000,
    status: "active",
    institutions: "Selected",
  },
  {
    id: 3,
    name: "Sports Excellence",
    eligibility: "State Level",
    amount: 40000,
    status: "active",
    institutions: "All",
  },
  {
    id: 4,
    name: "Cultural Award",
    eligibility: "National Level",
    amount: 35000,
    status: "inactive",
    institutions: "Selected",
  },
];

type ScholarshipRow = (typeof mockScholarships)[number];

export default function Scholarships() {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [scholarships] = useState(mockScholarships);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const totalItems = scholarships.length;
  const totalPages = Math.ceil(totalItems / pageSize);
  const paginatedScholarships = scholarships.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const handleAddScholarship = () => {
    toast.success("Scholarship added successfully!");
    setIsDialogOpen(false);
  };

  const tableColumns = useMemo<TableColumnDef<ScholarshipRow>[]>(
    () => [
      {
        id: "sr",
        label: "Sr. No",
        width: 72,
        alwaysVisible: true,
        renderCell: (_row, rowIndex) =>
          (currentPage - 1) * pageSize + rowIndex + 1,
      },
      {
        id: "name",
        label: "Scholarship Name",
        width: 200,
        renderCell: s => <span className="font-medium">{s.name}</span>,
      },
      {
        id: "eligibility",
        label: "Eligibility",
        width: 140,
        renderCell: s => s.eligibility,
      },
      {
        id: "amount",
        label: "Amount",
        width: 120,
        renderCell: s => `₹${s.amount.toLocaleString()}`,
      },
      {
        id: "applicableTo",
        label: "Applicable To",
        width: 140,
        renderCell: s => s.institutions,
      },
      {
        id: "status",
        label: "Status",
        width: 100,
        renderCell: s => (
          <Badge variant={s.status === "active" ? "default" : "secondary"}>
            {s.status}
          </Badge>
        ),
      },
      {
        id: "actions",
        label: "Action",
        width: 100,
        alwaysVisible: true,
        renderCell: () => (
          <div className="flex gap-2">
            <Button size="sm" variant="outline">
              <Edit className="h-4 w-4" />
            </Button>
            <Button size="sm" variant="outline">
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ),
      },
    ],
    [currentPage, pageSize]
  );

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">
            Scholarships Management
          </h1>
          <p className="text-muted-foreground">
            Manage scholarship programs and eligibility
          </p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button className="gap-2">
              <Plus className="h-4 w-4" />
              Add Scholarship
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Add New Scholarship</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="name">Scholarship Name</Label>
                <Input id="name" placeholder="Enter scholarship name" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="eligibility">Eligibility Criteria</Label>
                  <Input id="eligibility" placeholder="E.g., Above 80%" />
                </div>
                <div>
                  <Label htmlFor="amount">Amount (₹)</Label>
                  <Input
                    id="amount"
                    type="number"
                    placeholder="50000"
                    min={0}
                    onWheel={e => e.currentTarget.blur()}
                    onKeyDown={e => {
                      if (["e", "E", "-", "+"].includes(e.key)) {
                        e.preventDefault();
                      }
                    }}
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  placeholder="Enter scholarship details"
                  rows={3}
                />
              </div>
              <div>
                <Label htmlFor="institutions">Applicable Institutions</Label>
                <Select>
                  <SelectTrigger>
                    <SelectValue placeholder="Select institutions" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Institutions</SelectItem>
                    <SelectItem value="selected">
                      Selected Institutions
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="status">Status</Label>
                <Select defaultValue="active">
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => setIsDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button onClick={handleAddScholarship}>Save Scholarship</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardContent className="p-3 pb-4">
          <DataTable
            title={`All Scholarships (${scholarships.length})`}
            tableId="super-admin-scholarships"
            columns={tableColumns}
            data={paginatedScholarships}
            getRowKey={s => s.id}
          />

          {totalItems > 0 && (
            <div className="mt-4 border-t pt-4">
              <CommonPagination
                currentPage={currentPage}
                totalPages={totalPages}
                pageSize={pageSize}
                totalCount={totalItems}
                onPageChange={setCurrentPage}
                onPageSizeChange={size => {
                  setPageSize(size);
                  setCurrentPage(1);
                }}
              />
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
